import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { createDeal } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";
import { useUser } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface DeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  dealName: string;
  workspaceId: string;
  onSuccess?: () => void;
}

export function DeleteDialog({
  isOpen,
  onClose,
  dealId,
  dealName,
  workspaceId,
  onSuccess,
}: DeleteDialogProps) {
  const { user } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);

  if (!user?.id) {
    throw new Error("User ID is not available");
  }

  const userId = user.id;

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      // First create a flagged company entry
      const response = await createDeal.createFlaggedCompany(
        workspaceId,
        dealId,
        userId
      );
      if (response.status !== "success") {
        throw new Error("Failed to create flagged company");
      }

      // Then delete the flagged company
      await createDeal.deleteFlaggedCompany(workspaceId, dealId);

      toast.success("Deal deleted");
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Failed to delete deal:", error);
      toast.error(getErrorMessage(error, "Failed to delete deal"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && isDeleting) return;
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This will permanently delete {dealName} and remove it from your
            deals list. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Deal"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
